import { ChangeEvent, useState } from "react"
import { styled } from '@mui/material/styles';
import { useLocation } from "react-router-dom"
import { useAppDispatch } from "./app/hooks"
import {
    Alert,
    AlertTitle,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import { Localized, useLocalization } from "@fluent/react"
import { showError, showSuccess } from "./features/notifications/notificationsSlice";
import { confirmAccount } from "./features/user/userSlice";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { LanguageToggle } from "./app/components/LanguageToggle";


const PREFIX = 'Login';

const classes = {
    root: `${PREFIX}-root`,
    paper: `${PREFIX}-paper`,
    logo: `${PREFIX}-logo`,
    text: `${PREFIX}-text`,
    backdrop: `${PREFIX}-backdrop`
};

const Root = styled('div')(({ theme }) => ({
    [`&.${classes.root}`]: {
        textAlign: 'center',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '400px',
        margin: 'auto',
    },

    [`& .${classes.paper}`]: {
        width: "100%",
        paddingTop: 32,
        paddingBottom: 32,
    },

    [`& .${classes.logo}`]: {
        pointerEvents: 'none',
        height: 300,
    },

    [`& .${classes.text}`]: {
        width: '80%',
    },

    [`& .${classes.backdrop}`]: {
        zIndex: theme.zIndex.drawer + 1,
        color: "#fff",
    }
}));


export interface LoginAPIError {
    success: boolean
    token: null | string
    errors: string[]
}

export const ConfirmAccount = () => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const { search } = useLocation()

    const [password, setPassword] = useState("");
    const [passwordConfirm, setPasswordConfirm] = useState("");

    const [errorPassword, setPasswordError] = useState("");
    const [errorPasswordList, setErrorPasswordList] = useState<{ title: string; params?: { count?: number } }[]>([]);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [mask, setMask] = useState<boolean>(false);
    const navigate = useNavigate()

    const onPasswordChange = (e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)
    const onPasswordConfirmChange = (e: ChangeEvent<HTMLInputElement>) => setPasswordConfirm(e.target.value)

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };


    function timeout(delay: number) {
        return new Promise(res => setTimeout(res, delay));
    }

    const clearErrors = () => {
        setPasswordError("")
    }

    const Validate = () => {
        let valResult = true;
        setErrorPasswordList([]);

        if (password !== '' && passwordConfirm !== '' && passwordConfirm !== password) {
            valResult = false;
            setPasswordError(l10n.getString("password-do-not-match", null, "_password not match"));
        }
        return valResult;
    }

    const handleSaveClick = async () => {
        clearErrors()
        if (!Validate()) {
            return;
        }
        if (search) {
            const params = new URLSearchParams(search)

            if (params.has("t") && params.has("u")) {
                try {
                    setMask(true)

                    const resultAction = await dispatch(confirmAccount({
                        user: params.get("u") as string,
                        password: password,
                        passwordConfirmation: passwordConfirm,
                        token: params.get("t") as string
                    }))

                    if (confirmAccount.fulfilled.match(resultAction)) {
                        dispatch(showSuccess("account-confirmation-sucesfully"))
                        await timeout(2000)
                        navigate('/login', { replace: true })
                    }

                    if (confirmAccount.rejected.match(resultAction)) {
                        const { payload } = resultAction
                        if (payload) {
                            switch (payload.kind) {
                                case "http":
                                    if (payload.body) {
                                        const errors = JSON.parse(payload.body);
                                        if(Array.isArray(errors)) {
                                            const passwordErrors = errors.filter((err: any) => err.title.startsWith('password-require'));
                                            setErrorPasswordList(passwordErrors);
                                        } else {
                                            const otherErrors = errors.errors.filter((err: any) => !err.startsWith('password-require'));
                                            dispatch(showError(otherErrors[0]));
                                        }
                                    }
                                    else {
                                        dispatch(showError("error"))
                                    } break
                                case "connection":
                                    dispatch(showError("connection-issue"))
                                    break
                            }
                        } else {
                            dispatch(showError("error"))
                        }
                    }

                }
                catch (error) {
                    dispatch(showError(error ?? "error-login-failed"))
                }
                finally {
                    setMask(false)
                }
            }
        }
    }
    return (
        <Root className={classes.root}>
            <Paper variant="elevation" elevation={4} component="div" className={classes.paper} style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 10 }}>
                    <LanguageToggle />
                </div>
                <Grid container direction="column" spacing={2} sx={{ marginTop: 2 }}>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                            <Localized id="email-confirmation-message">
                                <span>Dziękujemy za potwierdzenie adresu email!</span>
                            </Localized>
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                            <Localized id="set-new-password">
                                <span>Ustal nowe hasło</span>
                            </Localized>
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            type={showPassword ? 'text' : 'password'}
                            className={classes.text}
                            onBlur={clearErrors}
                            onChange={onPasswordChange}
                            error={!!errorPassword || errorPasswordList.length > 0}
                            helperText={errorPasswordList.length > 0 ? l10n.getString("password-policy", null, "_Polityka bezpieczeństwa firmy wymaga, aby hasło spełniało poniższe kryteria") : errorPassword}
                            label={l10n.getString("password", null, "Nowe hasło")}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton tabIndex={-1} onClick={togglePasswordVisibility}>
                                            {showPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            type={showConfirmPassword ? 'text' : 'password'}
                            className={classes.text}
                            onChange={onPasswordConfirmChange}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton tabIndex={-1} onClick={toggleConfirmPasswordVisibility}>
                                            {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            label={l10n.getString("user-confirm-password", null, "Powtórz hasło")}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {errorPasswordList.length > 0 && (
                            <Alert 
                                severity="error" 
                                variant="standard"
                            >
                                <Box 
                                    component="ul" 
                                    sx={{ 
                                        m: 0, 
                                        pl: '20px',
                                        textAlign: 'left', 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        gap: '4px',
                                        fontSize: '0.875rem' 
                                    }}
                                >
                                    {errorPasswordList.map((err, index) => (
                                    <Box component="li" key={index} sx={{ color: 'inherit' }}>
                                        <Typography variant="body2" component="span">
                                        <Localized 
                                            id={err.title} 
                                            vars={{ ...(err.params || {}) }} 
                                        />
                                        </Typography>
                                    </Box>
                                    ))}
                                </Box>
                            </Alert>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Button onClick={handleSaveClick} color="primary" size="large" variant="contained">
                            <Localized id="save">
                                <span>Zapisz</span>
                            </Localized>
                        </Button>
                    </Grid>
                </Grid>
                <Backdrop className={classes.backdrop} open={mask}>
                    <CircularProgress color="inherit"></CircularProgress>
                </Backdrop>
            </Paper>
            <Box sx={{ marginTop: 2, }}>
                <img className={classes.logo} src={process.env.PUBLIC_URL + '/logo.gif'} alt="Logo" />
            </Box>
        </Root>
    );

}
